import React, { useEffect, useState } from 'react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import useRazorpay from '../hooks/useRazorpay';
import { getRazorpayMethodConfig, getRazorpayUpiPrefill, isValidUpiId } from '../utils/razorpay';
import { useLanguage } from '../contexts/LanguageContext';
import './CheckoutPage.css';

const CheckoutPage = () => {
  const { t } = useLanguage();
  useRazorpay(); // Load Razorpay script
  const { cartItems, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [shippingAddress, setShippingAddress] = useState({
    address: '', city: '', state: '', postalCode: '', country: 'India', phoneNo: ''
  });
  const [gstNumber, setGstNumber] = useState('');
  const [upiId, setUpiId] = useState('');
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const cartTotal = cartItems.reduce((acc, item) => acc + item.quantity * item.price, 0);

  // Smooth scroll to top on component mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleAddressChange = (e) => {
    setShippingAddress({ ...shippingAddress, [e.target.name]: e.target.value });
  };

  const toTranslationKey = (value = '') => value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  const getTranslatedProductName = (name) => (
    t(`bhaktiStore.products.${toTranslationKey(name)}.name`, {}, name)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setError(null);

    // Validate UPI ID
    if (!isValidUpiId(upiId)) {
      setError(t('checkout.errors.invalidUpi'));
      setIsProcessing(false);
      return;
    }

    try {
      // 1. Create the order in our database
      const orderData = {
        orderItems: cartItems.map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          image: item.images[0],
          product: item._id
        })),
        shippingAddress,
        gstNumber,
      };

      const orderResponse = await apiService.createStoreOrder(orderData);
      if (!orderResponse.success) throw new Error(orderResponse.message);

      const createdOrder = orderResponse.data;

      // 2. Create a Razorpay order
      const razorpayOrderResponse = await apiService.createRazorpayOrder({
        amount: createdOrder.totalAmount,
        currency: 'INR',
        entityId: createdOrder._id,
        entityType: 'order'
      });
      if (!razorpayOrderResponse.success) throw new Error(razorpayOrderResponse.message);

      const razorpayOrder = razorpayOrderResponse.data;

      // 3. Open Razorpay Checkout Config
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        name: 'Anandmaya Store',
        order_id: razorpayOrder.id,
        handler: async function (response) {
          // 4. Verify the payment (With robust internal error handling)
          try {
            const verificationData = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              entityId: createdOrder._id,
              entityType: 'order'
            };
            await apiService.verifyRazorpayPayment(verificationData);
            clearCart();
            // Redirect to the user's order history page on success
            navigate('/dashboard/orders');
          } catch (verifyErr) {
            setError(verifyErr.message || t('checkout.errors.unexpected', {}, 'Payment verification failed. Please contact support.'));
            setIsProcessing(false);
          }
        },
        method: getRazorpayMethodConfig(),
        prefill: getRazorpayUpiPrefill({
          name: user?.fullName,
          email: user?.email,
          contact: shippingAddress.phoneNo
        }, upiId),
        theme: { color: '#8B4513' },
        
        // Modal closure fix to unlock UI if user exits payment window
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
            setError(t('checkout.errors.paymentCancelled', {}, 'Payment was cancelled. You can click Pay to try again.'));
          }
        }
      };

      const paymentObject = new window.Razorpay(options);
      
      // Handle explicit checkout failures
      paymentObject.on('payment.failed', function (response) {
        setError(`${t('checkout.errors.paymentFailedPrefix')}: ${response.error.description}`);
        setIsProcessing(false);
      });

      paymentObject.open();

    } catch (err) {
      setError(err.message || t('checkout.errors.unexpected'));
      setIsProcessing(false); 
    }
  };

  if (cartItems.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '5rem', minHeight: '60vh' }}>
        <h2>{t('checkout.emptyCartTitle')}</h2>
        <Link to="/bhakti-store" className="btn btn-primary">{t('checkout.continueShopping')}</Link>
      </div>
    );
  }

  return (
    <div className="checkout-container">
      <div className="checkout-page-content">
        <h1>{t('checkout.title')}</h1>
        <div className="checkout-layout">
          <form onSubmit={handleSubmit} className="shipping-form">
            <h3>{t('checkout.form.shippingAddress')}</h3>
            <input name="address" placeholder={t('checkout.form.address')} onChange={handleAddressChange} required />
            <input name="city" placeholder={t('checkout.form.city')} onChange={handleAddressChange} required />
            <input name="state" placeholder={t('checkout.form.state')} onChange={handleAddressChange} required />
            <input name="postalCode" placeholder={t('checkout.form.postalCode')} onChange={handleAddressChange} required />
            <input name="country" value={t('checkout.form.countryDisplay', {}, 'India')} readOnly />
            <input name="phoneNo" type="tel" placeholder={t('checkout.form.phoneNumber')} onChange={handleAddressChange} required />

            <div className="gst-input-container">
              <label htmlFor="gstNumber">{t('checkout.form.gstNumberLabel')}</label>
              <input 
                id="gstNumber"
                name="gstNumber" 
                type="text" 
                placeholder={t('checkout.form.gstNumberPlaceholder')}
                value={gstNumber}
                onChange={(e) => setGstNumber(e.target.value)} 
              />
            </div>
            
            <div className="gst-input-container">
              <label htmlFor="upiId">{t('checkout.form.upiIdLabel')}</label>
              <input
                id="upiId"
                name="upiId"
                type="text"
                placeholder={t('checkout.form.upiIdPlaceholder')}
                value={upiId}
                onChange={(e) => setUpiId(e.target.value)}
              />
            </div>
            
            <button type="submit" disabled={isProcessing}>
              {isProcessing ? t('checkout.actions.processing') : t('checkout.actions.pay', { amount: cartTotal.toFixed(2) })}
            </button>
            
            {error && <p className="error-message">{error}</p>}
          </form>
          
          <div className="order-summary">
            <h3>{t('checkout.orderSummary.title')}</h3>
            {cartItems.map(item => (
              <div key={item._id} className="summary-item">
                <span>{t('checkout.orderSummary.itemLine', { name: getTranslatedProductName(item.name), quantity: item.quantity })}</span>
                <span>₹{(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
            <hr />
            <div className="summary-total">
              <strong>{t('checkout.orderSummary.total')}</strong>
              <strong>₹{cartTotal.toFixed(2)}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;